import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { LoginRequired } from '../../decorators/login.required';
import { NavController, NavParams } from 'ionic-angular';
import { AuthService } from '../../services/auth.service';
import { ApiCollectionResponse, ApiViewData, Conversation, DirectMessage, MessagesGroup, User } from '../../app/app.interfaces';
import { ApiService } from '../../services/api.service';
import { IonicHelper } from '../../helpers/ionic.helper';
import { ChatService } from '../../services/chat.service';
import { EventService } from '../../services/event.service';
import { PageTitleService } from "../../services/page-title.service";
import { Subscription } from "rxjs";
import { fromEvent } from "rxjs/observable/fromEvent";
import { throttleTime } from "rxjs/operators";
import { Attachment } from '../../partials/upload/attachment';
import { ButtonStyle } from '../../partials/upload/file-upload';


@Component({
  selector: 'chat-page',
  templateUrl: 'chat-page.html'
})
@LoginRequired()
export class ChatPage implements OnDestroy {
  
  @ViewChild('chat_input') messageInput: ElementRef;
  @ViewChild('messages_list') messagesList: ElementRef;

  private messageInputNative: HTMLInputElement;
  
  private allMessages: DirectMessage[] = [];
  public messagesGroups: MessagesGroup[] = [];

  public firstLoadViewData: ApiViewData;

  public textMessage = '';
  
  public conversations: Conversation[] = [];
  public isNewConversation = false;
  
  public userSearchResults: User[] = [];
  public userSearchTerm: string = '';
  public userView: ApiViewData = {};
  private currentUser: User;
  
  private withUserId: string = null;
  public withUser: User;

  private POLLING_INTERVAL = 2000;
  private pollingIntervalID: any = null;

  private filters: any = {};

  private loadingMessages = false;
  private sendingMessage = false;

  mobileTitle = 'chat-main';
  
  private subscriptions: Subscription[] = [];
  attachments: Attachment[] = [];
  buttonStylePaperclip = ButtonStyle.Paperclip;
  buttonStyleButton = ButtonStyle.Button;

  constructor(private nav: NavController,
              private authService: AuthService,
              private chatService: ChatService,
              private apiService: ApiService,
              private ionic: IonicHelper,
              private pageTitle: PageTitleService,
              private eventService: EventService,
              private navParams: NavParams) {

    this.pageTitle.setPageTitle('chat-main');
    
    const userId = this.navParams.get('userId');

    if (userId) {
      this.setReceiver(userId);
    }
    
    authService.afterInit().then(() => {
      this.currentUser = this.authService.user;
    });

    this.chatService.getConversations().subscribe((response: ApiCollectionResponse) => {
      this.conversations = response.items;

      if(this.withUserId) {
        const currentChatIsInAConversations = this.conversations.some(conv => {
          return conv.with === this.withUserId;
        });
        this.isNewConversation = !currentChatIsInAConversations;
      }

    });

    if (this.ionic.keyboardShown$) {
      const keyboardSub = this.ionic.keyboardShown$.subscribe((shown) => {
        this.scrollToBottom(0);
      });
      this.subscriptions.push(keyboardSub);
    }
  }

  ngOnDestroy(): void {
    for(let subscription of this.subscriptions){
      subscription.unsubscribe();
    }
  }
  loadOlderMessages() {
    if (this.withUserId && this.firstLoadViewData && !!this.firstLoadViewData.pages && !!this.firstLoadViewData.pages.next) {

      this.firstLoadViewData.loading = true;
      this.chatService.getMessagesFromPage(this.withUserId, this.firstLoadViewData.pages.next).subscribe((result) => {
        this.setMessages([...result.items, ...this.allMessages], false);
        this.firstLoadViewData = result.view;
      });
    }
  }

  textareaKeypress(ev: KeyboardEvent){
    if (ev.code === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();      
      this.sendMessage();
    }
  }

  sendMessage() {
    if (this.textMessage.trim() || this.attachments.length > 0) {
      if (this.sendingMessage) return;
      this.sendingMessage = true;
      this.scrollToBottom(0);
      this.chatService.sendMessage(this.textMessage, this.withUserId, this.attachments).then(() => {
        this.loadMessages().then(() => {
          this.sendingMessage = false;
          this.attachments = [];
        });
      });
    }
    
    this.textMessage = '';
    this.focusOnMessageInput();
    this.autosetTextareaHeight();
  }
  
  private loadMessages(): Promise<void> {
    
    return new Promise<void>((resolve) => {

      if(this.loadingMessages) {
        resolve();
        return;
      }

      this.loadingMessages = true;
      
      this.chatService.getMessages(this.withUserId, this.filters).subscribe((response: ApiCollectionResponse) => {

        if(! this.firstLoadViewData) {
          this.firstLoadViewData = response.view;
        }

        if(response.items.length > 0) {
          this.setMessages([...this.allMessages, ...response.items]);
          this.scrollToBottom(100);//Wait for messages to render
        }

        setTimeout(() => {
          this.loadingMessages = false;
          resolve();
        }, 50);
        
      });
    });
    
    

  }


  private setMessages(messages: DirectMessage[], setFilters = true): void {
    this.allMessages = messages;
    this.messagesGroups = this.sortMessagesGroups();
    if(setFilters) {
      this.filters = {
        'id[gt]': this.allMessages[this.allMessages.length - 1].id
      };
    }
  }

  private startPolling() {
    if(this.pollingIntervalID) {
      clearInterval(this.pollingIntervalID);
    }
    this.pollingIntervalID = setInterval(() => {
      this.loadMessages();
    }, this.POLLING_INTERVAL);
  }



  private sortMessagesGroups(): MessagesGroup[] {

    if(! this.allMessages || this.allMessages.length ===0) {
      return [];
    }

    let groupIndex = 0;
    let lastNewDay = new Date(this.allMessages[0].created);
    let sortedMessagesGroups: MessagesGroup[] = [{
      messages: [],
      date: lastNewDay
    }];

    this.allMessages.forEach((message: DirectMessage) => {
      if (this.isNewDay(message.created, lastNewDay)) {
        lastNewDay = new Date(message.created);
        sortedMessagesGroups.push({
          messages: [],
          date: lastNewDay
        });
        groupIndex++;
      }
      sortedMessagesGroups[groupIndex].messages.push(message);
    });
    
    return sortedMessagesGroups;

  }
  
  private isNewDay(dateTime: string, lastNewDay: Date): boolean {
    const dateOfMessage = new Date(dateTime);
    if (!lastNewDay) {
      lastNewDay = dateOfMessage;
    }

    let isNewDay = false;

    if (dateOfMessage.getFullYear() > lastNewDay.getFullYear()) {
      // Message is of new year
      isNewDay = true;
    } else if (dateOfMessage.getFullYear() === lastNewDay.getFullYear()) {
      // Message is of same year
      if (dateOfMessage.getMonth() > lastNewDay.getMonth()) {
        // Message is of new month in same year
        isNewDay = true;
      } else if (dateOfMessage.getMonth() === lastNewDay.getMonth() &&
        dateOfMessage.getDate() > lastNewDay.getDate()) {
        // Message is of new day in same month and year
        isNewDay = true;
      }
    }

    return isNewDay;
  }
  
  private setReceiver(withUserId: string = null) {

    this.withUserId = !withUserId.toString().includes('users') ? '/users/' + withUserId : withUserId;
    
    if (!this.withUserId) {
      return;
    }
    this.updateWithUser(this.withUserId);
    
    this.setupMessageInputListener();
    this.loadMessages();
    this.startPolling();
  }
  
  private updateWithUser(withUser: string) {
    const withUserId = withUser.substring(withUser.lastIndexOf('/') + 1);
    
    this.apiService.getResourceById('users', parseInt(withUserId, 10)).then((user: User) => {
      this.withUser = user;
      this.mobileTitle = 'chat';
      this.pageTitle.setPageTitle('chat', user);
    });
  }
  
  private setupMessageInputListener() {
    if(! this.messageInput) {
      console.log('messageInput NA');
      setTimeout(() => {
        this.setupMessageInputListener();
      }, 100);
      return;
    }

    //Presetup
    this.messageInputNative = this.messageInput.nativeElement;
    const keyboardSub = fromEvent(this.messageInputNative , 'keyup').pipe(
      throttleTime(50)
    ).subscribe((ev: KeyboardEvent) => {
      this.autosetTextareaHeight();
    });
    this.subscriptions.push(keyboardSub);    
  }
  
  private autosetTextareaHeight() {
    if(this.messageInputNative) {
      this.messageInputNative.style.overflow = 'hidden';
      this.messageInputNative.style.height = '0px';
      this.messageInputNative.style.height = this.messageInputNative.scrollHeight + 'px';
      this.scrollToBottom(0);
    }
  }

  private focusOnMessageInput() {
    if (this.messageInputNative) {
      this.messageInputNative.focus();
    }
  }

  private scrollToBottom(timeout=150) {
    setTimeout(() => {
      if(!!this.messagesList && !!this.messagesList.nativeElement) {
        this.messagesList.nativeElement.scrollTop = this.messagesList.nativeElement.scrollHeight;
      }
    }, timeout);
  }

  userSearch(): void{
    if(this.userSearchTerm) {
      this.userSearchResults = [];
      this.loadUsers(this.currentUser, 'users', { fullName: this.userSearchTerm }, false);
    }
    else {
      this.loadUsers(this.currentUser);
    }
  }


  clearUserSearch() {
    this.userView = {};
    this.userSearchTerm = '';
    this.userSearchResults = [];
  }

  loadUsers(currentUser: User, uri: string = 'users', filters: any = null, append: boolean = false): void {

    this.userView.loading = true;

    this.apiService.getCollection(uri, filters).then((apiResponse: ApiCollectionResponse) => {
      
      const filteredUsers = apiResponse.items.filter(user => {
        return !this.userExistsInConversations(user['@id']) && user.id !== currentUser.id
      });

      if(append) {
        this.userSearchResults = [...this.userSearchResults, ...filteredUsers];
      }
      else {
        this.userSearchResults = filteredUsers
      }

      this.userView.loading = false;
      this.userView = apiResponse.view;

    }).catch((err) => {
      console.error(err);
      this.ionic.alert('GLOBAL.GENERAL_ERROR');
    });
  }
  
  private userExistsInConversations(userId: string): boolean {
    return this.conversations.filter(conversation =>
      userId === conversation.with).length > 0;
  }

  usersLoadMore(): void {
    this.loadUsers(this.currentUser, this.userView.pages.next, null, true);
  }
  
  ionViewWillLeave() {
    // this.chatService.stopPollingDirectMessages(this.withUserId);
    if(this.pollingIntervalID) {
      clearInterval(this.pollingIntervalID);
    }
  }

  addAttachment(event) {
    this.attachments.push(event);
    let tmpAttachments = this.attachments;
    this.attachments = tmpAttachments;
  }

  attachmentUploadError(ev){
    this.ionic.alert('POST_CREATE.ADD_ATTACHMENT_ERROR');
  }

  removeAttachmentField(i): void {
    this.attachments.splice(i, 1);
  }
}



// WEBPACK FOOTER //
// ./src/components/chat-page/chat-page.ts